/**
 * Customer-facing weekly report contracts (RBAC area: `reports`; mutations admin-only).
 *
 * A customer report is an admin-authored layout (HTML template with placeholder tokens)
 * plus a weekly schedule. One definition is rendered SEPARATELY for each enrolled
 * customer, filtered to that customer's own data (open projects with budget/hours,
 * managed-services agreements, open and recently-closed tickets with ticket hours, and
 * hours-by-person). Each enrolled customer chooses which of its contacts receive the
 * email — the whole thing is managed from the one Customer Reports page.
 *
 * Keep this file free of runtime framework dependencies (shared by backend + frontend).
 */
/** Days of the week for the weekly schedule (0 = Sunday … 6 = Saturday). */
export const DAY_OF_WEEK_LABELS = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];
/**
 * Placeholder tokens a customer-report template may reference.
 *
 *   - `{{token}}`            — HTML-escaped scalar substitution.
 *   - `{{{token}}}`          — raw substitution (generated markup such as the tables).
 *   - `{{#token}}…{{/token}}` — a section kept only when `token` is non-empty.
 *
 * The scalar/table tokens below are always available; the `include*` section tokens are
 * present only when the matching section toggle is on (use them to gate a whole section).
 */
export const CUSTOMER_REPORT_TOKENS = [
    'companyName',
    'customerName',
    'reportDate',
    'periodLabel',
    // Summary scalars.
    'openProjectCount',
    'projectHoursLastWeek',
    'projectHoursThisMonth',
    'openTicketCount',
    'closedTicketCount',
    'ticketHoursTotal',
    'managedServicesCount',
    'totalHoursThisMonth',
    // Generated tables (raw).
    'projectsTable',
    'paygTable',
    'agreementsTable',
    'ticketsTable',
    'hoursByPersonTable',
    // Section gates (truthy when the matching toggle is enabled).
    'includeProjects',
    'includePayg',
    'includeAgreements',
    'includeTickets',
];
export const CUSTOMER_REPORT_RUN_STATUSES = ['ok', 'failed'];
// ---------------------------------------------------------------------------
// Default template (used to pre-fill the editor when creating a new report).
// ---------------------------------------------------------------------------
export const DEFAULT_CUSTOMER_REPORT_NAME = 'Weekly Status Report';
export const DEFAULT_CUSTOMER_REPORT_SUBJECT = '{{companyName}} Weekly Status Report — {{customerName}} ({{reportDate}})';
export const DEFAULT_CUSTOMER_REPORT_HTML = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #1f2937; margin: 0; padding: 24px; }
  h1 { font-size: 22px; margin: 0 0 2px; color: #334155; }
  h2 { font-size: 16px; margin: 24px 0 4px; color: #334155; }
  .lede { color: #6b7280; font-size: 13px; margin: 0 0 16px; }
  .stats { margin: 12px 0 4px; }
  .stat { display: inline-block; margin-right: 28px; }
  .stat .n { font-size: 26px; font-weight: 700; color: #334155; }
  .stat .l { font-size: 12px; color: #6b7280; }
  hr { border: none; border-top: 1px solid #e5e7eb; margin: 16px 0; }
</style>
</head>
<body>
  <h1>{{companyName}} — Projects &amp; Ticket Activity</h1>
  <p class="lede">Prepared for <strong>{{customerName}}</strong> &middot; {{periodLabel}} &middot; Created on {{reportDate}}</p>
  <hr />

  {{#includeProjects}}
  <h2>Open Projects</h2>
  <div class="stats">
    <span class="stat"><span class="n">{{openProjectCount}}</span> <span class="l">Open Projects</span></span>
    <span class="stat"><span class="n">{{projectHoursLastWeek}}</span> <span class="l">Project Hours Last Week</span></span>
    <span class="stat"><span class="n">{{projectHoursThisMonth}}</span> <span class="l">Project Hours This Month</span></span>
  </div>
  {{{projectsTable}}}
  {{/includeProjects}}

  {{#includePayg}}
  <h2>Pay-As-You-Go Support Agreements</h2>
  {{{paygTable}}}
  {{/includePayg}}

  {{#includeAgreements}}
  <h2>Managed Services Agreements</h2>
  {{{agreementsTable}}}
  {{/includeAgreements}}

  {{#includeTickets}}
  <h2>Ticket Activity</h2>
  <div class="stats">
    <span class="stat"><span class="n">{{openTicketCount}}</span> <span class="l">Open Tickets</span></span>
    <span class="stat"><span class="n">{{closedTicketCount}}</span> <span class="l">Recently Closed</span></span>
    <span class="stat"><span class="n">{{ticketHoursTotal}}</span> <span class="l">Ticket Hours</span></span>
  </div>
  {{{ticketsTable}}}
  {{/includeTickets}}

  <h2>Hours by Person this Month</h2>
  {{{hoursByPersonTable}}}
</body>
</html>`;
// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------
/** A report recipient is either a customer contact or an internal PSA member. */
export const CUSTOMER_REPORT_RECIPIENT_TYPES = ['contact', 'member'];
//# sourceMappingURL=customer-report.js.map